import { useCallback, useEffect, useMemo, useState } from 'react';
import { type MessageDescriptor } from 'react-intl-next';

import type {
	OrganisationSettingValue,
	RepositorySettingValue,
} from '../../../../../../typings/globals';
import authRequest from '../../../../utils/fetch';
import messages from '../components/messages';
import { parseStringBoolUndefined } from '../utils';

import { SET_CODE_REVIEWER_SETTING_URL } from './url';

type InputProps = {
	entity: GH.Organisation | GH.Repository;
	onToggle?: (newValue: boolean) => void;
};
type ReturnType = {
	isEnabled: boolean;
	updateSetting: (newValue: boolean) => Promise<void>;
	error: MessageDescriptor | null;
};
// This hook is used to toggle the code reviewer setting for either an organisation or a repository.
// It uses optimistic updates to return the value that the user expects immediately for displaying in the UI
export const useToggleCodeReviewerSetting = ({ entity, onToggle }: InputProps): ReturnType => {
	const extractSetting = useMemo(() => {
		return 'repoId' in entity
			? parseStringBoolUndefined(entity.autoreviewSettings?.value.autoreview_enabled)
			: parseStringBoolUndefined(entity.autoreviewSettings?.value.autoreview_activated);
	}, [entity]);

	const [isEnabled, setIsEnabled] = useState<boolean>(extractSetting);
	const [error, setError] = useState<MessageDescriptor | null>(null);

	useEffect(() => {
		setIsEnabled(extractSetting);
	}, [extractSetting]);

	const getOrgSettingValue = (newValue: boolean): OrganisationSettingValue => ({
		autoreview_activated: newValue,
	});

	const getRepoSettingValue = (newValue: boolean): RepositorySettingValue => ({
		autoreview_acceptance_criteria_commit_trigger_enabled:
			(entity.autoreviewSettings?.value as RepositorySettingValue)
				.autoreview_acceptance_criteria_commit_trigger_enabled ?? true,
		autoreview_acceptance_criteria_enabled:
			(entity.autoreviewSettings?.value as RepositorySettingValue)
				.autoreview_acceptance_criteria_enabled ?? true,
		autoreview_enabled: newValue,
	});

	const updateSetting = useCallback(
		async (newValue: boolean) => {
			setError(null);
			// Optimistic Update
			setIsEnabled(newValue);

			if (onToggle) {
				onToggle(newValue);
			}

			const containerType = 'repoId' in entity ? 'repository' : 'workspace';
			const containerId = 'repoId' in entity ? entity.repoUrl : entity.ownerUrl;
			const value =
				'repoId' in entity ? getRepoSettingValue(newValue) : getOrgSettingValue(newValue);

			try {
				const res = await fetch(
					authRequest(SET_CODE_REVIEWER_SETTING_URL, {
						method: 'POST',
						body: JSON.stringify({
							settings: [
								{
									containerType,
									containerId,
									key: 'autoreview_settings',
									value,
								},
							],
						}),
						credentials: 'include',
						headers: {
							'Content-Type': 'application/json',
						},
					}),
				);

				if (!res.ok) {
					throw new Error(`http error status ${res.status} with text: ${res.statusText}`);
				}
			} catch (e) {
				setError(messages.errorUpdateCodeReviewerSettings);
				// reverse optimistic update
				setIsEnabled(!newValue);
			}
		},
		[onToggle, entity],
	);

	return {
		isEnabled,
		updateSetting,
		error,
	};
};
